import axios, { AxiosInstance } from "axios";

// Base URL - Use API Gateway like all other services
const API_BASE_URL =
  import.meta.env.VITE_RIDER_API_URL || "http://localhost:8000/api";

class ReferralService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Referral service error:",
          error.response?.data || error.message
        );
        return Promise.reject(error);
      }
    );
  }

  // Get all referrals with filters
  async getReferrals(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sourcingChannel?: string;
    vendorId?: string;
    telecallerId?: string;
    city?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }) {
    const response = await this.api.get("/riders/referrals", { params });
    return response.data;
  }

  // Get referral by ID
  async getReferralById(id: string) {
    const response = await this.api.get(`/riders/referrals/${id}`);
    return response.data;
  }

  // Create new referral
  async createReferral(data: {
    candidateName: string;
    candidatePhone: string;
    candidateEmail?: string;
    city: string;
    sourcingChannel: string;
    vendorId?: string;
    telecallerId?: string;
    notes?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  }) {
    const response = await this.api.post("/riders/referrals", data);
    return response.data;
  }

  // Update referral
  async updateReferral(id: string, data: { notes?: string }) {
    const response = await this.api.put(`/riders/referrals/${id}`, data);
    return response.data;
  }

  // Validate referral code
  async validateReferralCode(code: string) {
    const response = await this.api.get(`/riders/referrals/validate/${code}`);
    return response.data;
  }

  // Get referral conversion funnel analytics
  async getReferralFunnel(params?: {
    vendorId?: string;
    telecallerId?: string;
    city?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const response = await this.api.get("/riders/referrals/analytics/funnel", {
      params,
    });
    return response.data;
  }

  // Internal tracking endpoints (for testing/admin)
  async trackRegistration(referralCode: string, riderId: string) {
    const response = await this.api.post(
      "/riders/referrals/internal/track-registration",
      {
        referralCode,
        riderId,
      }
    );
    return response.data;
  }

  async trackKycVerified(riderId: string) {
    const response = await this.api.post(
      "/riders/referrals/internal/track-kyc",
      {
        riderId,
      }
    );
    return response.data;
  }

  async trackFirstDelivery(riderId: string) {
    const response = await this.api.post(
      "/riders/referrals/internal/track-first-delivery",
      {
        riderId,
      }
    );
    return response.data;
  }

  async trackRetention(riderId: string, days: number) {
    const response = await this.api.post(
      "/riders/referrals/internal/track-retention",
      {
        riderId,
        days,
      }
    );
    return response.data;
  }
}

export const referralService = new ReferralService();
export default referralService;
